import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Users, Shield, Star, ChevronRight, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';

const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    date: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Demande envoyée!",
      description: "Nous vous répondrons dans les 2 heures.",
    });
    setFormData({
      name: '',
      phone: '',
      email: '',
      service: '',
      date: '',
      message: ''
    });
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header/Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3">
            <div className="flex items-center">
              <img
                src="https://customer-assets.emergentagent.com/job_aigle-taxis/artifacts/wshzq3zf_taxi_aigle_logo_transparent_cropped.png"
                alt="TAXI AIGLE"
                className="h-14 w-auto"
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-[#B8935C] transition-colors font-medium text-sm">
                Services
              </button>
              <button onClick={() => scrollToSection('fleet')} className="text-gray-700 hover:text-[#B8935C] transition-colors font-medium text-sm">
                Flotte
              </button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-[#B8935C] transition-colors font-medium text-sm">
                À Propos
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-[#B8935C] transition-colors font-medium text-sm">
                Contact
              </button>
              <Button onClick={() => scrollToSection('contact')} size="sm" className="bg-[#B8935C] hover:bg-[#A07D4A] text-white">
                <Phone className="w-4 h-4 mr-2" />
                079 779 63 83
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-gray-700"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <nav className="md:hidden py-4 space-y-4 border-t">
              <button onClick={() => scrollToSection('services')} className="block w-full text-left text-gray-700 hover:text-[#B8935C] transition-colors font-medium py-2">
                Services
              </button>
              <button onClick={() => scrollToSection('fleet')} className="block w-full text-left text-gray-700 hover:text-[#B8935C] transition-colors font-medium py-2">
                Flotte
              </button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left text-gray-700 hover:text-[#B8935C] transition-colors font-medium py-2">
                À Propos
              </button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left text-gray-700 hover:text-[#B8935C] transition-colors font-medium py-2">
                Contact
              </button>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto text-center pt-12">
          <img
            src="https://customer-assets.emergentagent.com/job_aigle-taxis/artifacts/wshzq3zf_taxi_aigle_logo_transparent_cropped.png"
            alt="TAXI AIGLE"
            className="h-64 md:h-80 lg:h-96 w-auto mx-auto mb-8"
          />
          <p className="text-xl md:text-2xl font-bold mb-3 text-[#B8935C]">
            SERVICE PREMIUM
          </p>
          <p className="text-lg md:text-xl mb-3 text-gray-700">
            Votre service de transport haut de gamme à Aigle
          </p>
          <p className="text-base md:text-lg mb-8 text-gray-600 italic">
            Dédié. Professionnel. Personnel.
          </p>
          <p className="text-base mb-10 text-gray-600 max-w-2xl mx-auto">
            Votre service de taxi à Aigle et toute la Suisse
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => scrollToSection('contact')}
              size="lg"
              className="bg-[#B8935C] hover:bg-[#A07D4A] text-white px-8 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              RÉSERVER MAINTENANT
            </Button>
            <Button
              onClick={() => scrollToSection('contact')}
              size="lg"
              variant="outline"
              className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white px-8 py-6 text-base font-semibold transition-all"
            >
              DEMANDER UN DEVIS
            </Button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              CE QUE NOUS FAISONS
            </h2>
            <p className="text-lg font-bold text-gray-700 mb-2">
              SERVICE TAXI, TRANSFERTS AÉROPORT, NAVETTES ÉVÉNEMENTS - TOUT LE RESTE?
            </p>
            <p className="text-base text-gray-600 max-w-4xl mx-auto">
              Avec plus de 12 ans d'expérience, nous offrons des services de chauffeur de première classe sur mesure à Aigle, dans tout le canton de Vaud et en Suisse.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="overflow-hidden border hover:shadow-xl transition-all duration-300 group">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1564181064972-432b6f96c0ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBNZXJjZWRlcyUyMGxpbW91c2luZXxlbnwwfHx8fDE3NTQ5OTQ4OTh8MA&ixlib=rb-4.1.0&q=85"
                  alt="Transferts Aéroport"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <CardHeader className="pb-3">
                <CardTitle className="text-xl text-gray-900 font-bold">TRANSFERTS AÉROPORT</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <CardDescription className="text-gray-600 mb-4 text-sm">
                  Service de transfert ponctuel et confortable vers tous les aéroports suisses et internationaux
                </CardDescription>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">Genève</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">Zurich</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">International</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">Swiss</span>
                </div>
                <Button
                  variant="link"
                  className="text-[#B8935C] hover:text-[#A07D4A] p-0 h-auto font-semibold text-sm group/btn"
                  onClick={() => scrollToSection('contact')}
                >
                  En savoir plus
                  <ChevronRight className="w-4 h-4 ml-1 group-hover/btn:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>

            <Card className="overflow-hidden border hover:shadow-xl transition-all duration-300 group">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1647925692939-f91260d37287?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHN1aXQlMjBkcml2ZXJ8ZW58MHx8fHwxNzU0OTk0OTEwfDA&ixlib=rb-4.1.0&q=85"
                  alt="Service Taxi"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <CardHeader className="pb-3">
                <CardTitle className="text-xl text-gray-900 font-bold">SERVICE TAXI</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <CardDescription className="text-gray-600 mb-4 text-sm">
                  Transport VIP avec chauffeur professionnel pour vos déplacements d'affaires et privés
                </CardDescription>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">Mercedes S-Class</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">Chauffeur VIP</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">Sécurité & Confidentialité</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">PRESTIGE</span>
                </div>
                <Button
                  variant="link"
                  className="text-[#B8935C] hover:text-[#A07D4A] p-0 h-auto font-semibold text-sm group/btn"
                  onClick={() => scrollToSection('contact')}
                >
                  En savoir plus
                  <ChevronRight className="w-4 h-4 ml-1 group-hover/btn:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>

            <Card className="overflow-hidden border hover:shadow-xl transition-all duration-300 group">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1659611807210-917ac61ff451?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxTd2l0emVybGFuZCUyMGx1eHVyeXxlbnwwfHx8fDE3NTQ5OTQ5MTV8MA&ixlib=rb-4.1.0&q=85"
                  alt="Événements"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <CardHeader className="pb-3">
                <CardTitle className="text-xl text-gray-900 font-bold">ÉVÉNEMENTS & PLUS</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <CardDescription className="text-gray-600 mb-4 text-sm">
                  Service multi-fonctionnel pour mariages, galas, excursions et transport scolaire sécurisé
                </CardDescription>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">Événements</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">Événements</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">Événements</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">Transport Scolaire Sécurisé</span>
                </div>
                <Button
                  variant="link"
                  className="text-[#B8935C] hover:text-[#A07D4A] p-0 h-auto font-semibold text-sm group/btn"
                  onClick={() => scrollToSection('contact')}
                >
                  En savoir plus
                  <ChevronRight className="w-4 h-4 ml-1 group-hover/btn:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Features Row */}
          <div className="grid md:grid-cols-3 gap-8 mt-16 text-center">
            <div>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                <Clock className="w-8 h-8 text-[#B8935C]" />
              </div>
              <h3 className="text-lg font-bold mb-2 text-gray-900">7/7 Disponible</h3>
              <p className="text-gray-600 text-sm">
                Service disponible 7 jours sur 7 pour tous vos besoins
              </p>
            </div>
            <div>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                <Users className="w-8 h-8 text-[#B8935C]" />
              </div>
              <h3 className="text-lg font-bold mb-2 text-gray-900">Chauffeurs Experts</h3>
              <p className="text-gray-600 text-sm">
                Chauffeurs professionnels, multilingues et parfaitement discrets
              </p>
            </div>
            <div>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                <MapPin className="w-8 h-8 text-[#B8935C]" />
              </div>
              <h3 className="text-lg font-bold mb-2 text-gray-900">Toute la Suisse</h3>
              <p className="text-gray-600 text-sm">
                Genève, Lausanne, Zurich, Gstaad, stations de ski et plus encore
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={() => scrollToSection('contact')}
              size="lg"
              className="bg-[#B8935C] hover:bg-[#A07D4A] text-white px-8 py-6 text-base font-semibold"
            >
              DEMANDER UN DEVIS
            </Button>
          </div>
        </div>
      </section>

      {/* Fleet Section */}
      <section id="fleet" className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              NOTRE FLOTTE PREMIUM
            </h2>
            <p className="text-base text-gray-600 max-w-4xl mx-auto">
              Une flotte diversifiée de véhicules haut de gamme pour répondre à tous vos besoins de transport, du taxi standard à l'autocar de luxe.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Mercedes Classe S */}
            <Card className="overflow-hidden border hover:shadow-xl transition-all duration-300 group">
              <div className="aspect-video overflow-hidden bg-gray-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_swisslux-service/artifacts/h5z3l7c2_IMG_6713.WEBP"
                  alt="Mercedes Classe S"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start mb-1">
                  <span className="px-3 py-1 bg-[#B8935C] text-white rounded text-xs font-bold">PRESTIGE</span>
                </div>
                <CardTitle className="text-xl text-gray-900 font-bold">Mercedes Classe S</CardTitle>
              </CardHeader>
              <CardContent className="pt-2">
                <p className="text-gray-600 mb-4 text-sm">
                  Le summum du luxe et du confort pour vos déplacements les plus prestigieux
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    1-3 passagers
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    3 bagages
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Cuir premium
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Climatisation
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Wi-Fi
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Minibar
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Mercedes Classe E */}
            <Card className="overflow-hidden border hover:shadow-xl transition-all duration-300 group">
              <div className="aspect-video overflow-hidden bg-gray-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_limo-service-1/artifacts/6vtkm6d1_IMG_6716.WEBP"
                  alt="Mercedes Classe E"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start mb-1">
                  <span className="px-3 py-1 bg-[#B8935C] text-white rounded text-xs font-bold">CONFORT</span>
                </div>
                <CardTitle className="text-xl text-gray-900 font-bold">Mercedes Classe E</CardTitle>
              </CardHeader>
              <CardContent className="pt-2">
                <p className="text-gray-600 mb-4 text-sm">
                  Élégance et performance pour vos déplacements d'affaires et privés
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    1-3 passagers
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    3 bagages
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Intérieur premium
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Navigation GPS
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Système audio
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Confort optimal
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Mercedes Classe V */}
            <Card className="overflow-hidden border hover:shadow-xl transition-all duration-300 group">
              <div className="aspect-video overflow-hidden bg-gray-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_limo-service-1/artifacts/g1luyze6_IMG_6715.WEBP"
                  alt="Mercedes Classe V"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start mb-1">
                  <span className="px-3 py-1 bg-[#B8935C] text-white rounded text-xs font-bold">GROUPE</span>
                </div>
                <CardTitle className="text-xl text-gray-900 font-bold">Mercedes Classe V</CardTitle>
              </CardHeader>
              <CardContent className="pt-2">
                <p className="text-gray-600 mb-4 text-sm">
                  Véhicule spacieux et confortable pour vos groupes jusqu'à 7 personnes
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Jusqu'à 7 passagers
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    7 bagages
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Sièges individuels
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Espace généreux
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Climatisation
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Confort famille
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Taxi Standard */}
            <Card className="overflow-hidden border hover:shadow-xl transition-all duration-300 group">
              <div className="aspect-video overflow-hidden bg-gray-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_limo-service-1/artifacts/zu6ufa8v_IMG_6724.PNG"
                  alt="Taxi Standard"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start mb-1">
                  <span className="px-3 py-1 bg-[#B8935C] text-white rounded text-xs font-bold">ÉCONOMIQUE</span>
                </div>
                <CardTitle className="text-xl text-gray-900 font-bold">Taxi Standard</CardTitle>
              </CardHeader>
              <CardContent className="pt-2">
                <p className="text-gray-600 mb-4 text-sm">
                  Solution rapide et économique pour vos trajets quotidiens
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    1-4 passagers
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    2 bagages
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Service rapide
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Tarif compétitif
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Ponctualité
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Confort standard
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Mercedes Sprinter */}
            <Card className="overflow-hidden border hover:shadow-xl transition-all duration-300 group">
              <div className="aspect-video overflow-hidden bg-gray-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_limo-service-1/artifacts/i6929vbu_IMG_6717.PNG"
                  alt="Mercedes Sprinter"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start mb-1">
                  <span className="px-3 py-1 bg-[#B8935C] text-white rounded text-xs font-bold">ÉVÉNEMENTS</span>
                </div>
                <CardTitle className="text-xl text-gray-900 font-bold">Mercedes Sprinter</CardTitle>
              </CardHeader>
              <CardContent className="pt-2">
                <p className="text-gray-600 mb-4 text-sm">
                  Minibus premium pour excursions et événements jusqu'à 16 personnes
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Jusqu'à 16 passagers
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    16+ bagages
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Grand espace
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Équipement moderne
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Sécurité renforcée
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Excursions
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Autocar */}
            <Card className="overflow-hidden border hover:shadow-xl transition-all duration-300 group">
              <div className="aspect-video overflow-hidden bg-gray-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_swisslux-service/artifacts/4efpc0tu_IMG_6718%202.AVIF"
                  alt="Autocar"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start mb-1">
                  <span className="px-3 py-1 bg-[#B8935C] text-white rounded text-xs font-bold">GRANDS GROUPES</span>
                </div>
                <CardTitle className="text-xl text-gray-900 font-bold">Autocar</CardTitle>
              </CardHeader>
              <CardContent className="pt-2">
                <p className="text-gray-600 mb-4 text-sm">
                  Autocar premium pour grands groupes et voyages organisés
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Jusqu'à 50 passagers
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Soutes spacieuses
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Confort premium
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Toilettes
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Wi-Fi
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#B8935C] mr-2"></span>
                    Système de divertissement
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Services Spéciaux */}
          <div className="mt-12">
            <h3 className="text-2xl font-bold mb-6 text-gray-900 text-center">Services Spéciaux</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-6 border hover:shadow-lg transition-all">
                <h4 className="text-lg font-bold mb-2 text-gray-900">Transport Scolaire Sécurisé</h4>
                <p className="text-gray-600 text-sm">
                  Minibus équipés de sièges enfants homologués et chauffeurs spécialisés pour le transport scolaire en toute sécurité.
                </p>
              </Card>
              <Card className="p-6 border hover:shadow-lg transition-all">
                <h4 className="text-lg font-bold mb-2 text-gray-900">Véhicules Adaptés PMR</h4>
                <p className="text-gray-600 text-sm">
                  Véhicules spécialement équipés pour le transport de personnes à mobilité réduite avec rampes d'accès et équipements adaptés.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              À PROPOS DE TAXI AIGLE
            </h2>
            <div className="max-w-4xl mx-auto text-base text-gray-600 space-y-3">
              <p>
                Taxi Aigle est votre partenaire de confiance pour tous vos besoins de transport haut de gamme à Aigle et dans toute la Suisse. Depuis plus de 12 ans, nous offrons un service d'exception alliant élégance, ponctualité et professionnalisme.
              </p>
              <p>
                Basés dans le magnifique canton de Vaud, nous desservons les destinations les plus prisées : <strong>Genève, Lausanne, Zurich, Gstaad, les stations de ski alpines</strong>, ainsi que tous les aéroports suisses et internationaux. Notre équipe de chauffeurs multilingues et notre flotte de véhicules premium vous garantissent une expérience de transport incomparable.
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
            <div className="text-center">
              <div className="text-4xl font-bold text-[#B8935C] mb-1">12+</div>
              <div className="text-gray-600 text-sm font-medium">Années d'Expérience</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#B8935C] mb-1">7/7</div>
              <div className="text-gray-600 text-sm font-medium">Service Disponible</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#B8935C] mb-1">10000+</div>
              <div className="text-gray-600 text-sm font-medium">Clients Satisfaits</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#B8935C] mb-1">1000+</div>
              <div className="text-gray-600 text-sm font-medium">Destinations</div>
            </div>
          </div>

          {/* Values */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card className="p-6 text-center border hover:shadow-lg transition-all">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gray-100 mb-4">
                <Shield className="w-7 h-7 text-[#B8935C]" />
              </div>
              <h3 className="text-lg font-bold mb-2 text-gray-900">Sécurité & Confidentialité</h3>
              <p className="text-gray-600 text-sm">
                Nos chauffeurs professionnels garantissent votre sécurité et respectent votre confidentialité avec la plus grande discrétion.
              </p>
            </Card>
            <Card className="p-6 text-center border hover:shadow-lg transition-all">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gray-100 mb-4">
                <Users className="w-7 h-7 text-[#B8935C]" />
              </div>
              <h3 className="text-lg font-bold mb-2 text-gray-900">Service Personnalisé</h3>
              <p className="text-gray-600 text-sm">
                Chaque trajet est adapté à vos besoins spécifiques. Notre équipe multilingue est à votre écoute 24h/24.
              </p>
            </Card>
            <Card className="p-6 text-center border hover:shadow-lg transition-all">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gray-100 mb-4">
                <Star className="w-7 h-7 text-[#B8935C]" />
              </div>
              <h3 className="text-lg font-bold mb-2 text-gray-900">Excellence & Ponctualité</h3>
              <p className="text-gray-600 text-sm">
                Nous nous engageons à vous offrir un service d'exception avec une ponctualité irréprochable pour tous vos déplacements.
              </p>
            </Card>
          </div>

          {/* Destinations */}
          <div>
            <h3 className="text-2xl font-bold mb-6 text-center text-gray-900">Nos Destinations Principales</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="p-5 border hover:shadow-lg transition-all">
                <h4 className="font-bold text-base mb-2 text-[#B8935C]">Aéroports</h4>
                <ul className="space-y-1 text-gray-600 text-sm">
                  <li>• Genève-Cointrin</li>
                  <li>• Zurich-Kloten</li>
                  <li>• Bâle-Mulhouse</li>
                  <li>• Sion</li>
                </ul>
              </Card>
              <Card className="p-5 border hover:shadow-lg transition-all">
                <h4 className="font-bold text-base mb-2 text-[#B8935C]">Villes Suisses</h4>
                <ul className="space-y-1 text-gray-600 text-sm">
                  <li>• Genève</li>
                  <li>• Lausanne</li>
                  <li>• Montreux</li>
                  <li>• Zurich</li>
                  <li>• Berne</li>
                  <li>• Lucerne</li>
                  <li>• Bâle</li>
                  <li>• Lugano</li>
                </ul>
              </Card>
              <Card className="p-5 border hover:shadow-lg transition-all">
                <h4 className="font-bold text-base mb-2 text-[#B8935C]">Stations de Ski</h4>
                <ul className="space-y-1 text-gray-600 text-sm">
                  <li>• Gstaad</li>
                  <li>• Verbier</li>
                  <li>• Crans-Montana</li>
                  <li>• Zermatt</li>
                  <li>• Villars</li>
                  <li>• Leysin</li>
                  <li>• Champéry</li>
                  <li>• Courchevel</li>
                </ul>
              </Card>
              <Card className="p-5 border hover:shadow-lg transition-all">
                <h4 className="font-bold text-base mb-2 text-[#B8935C]">Villes Internationales</h4>
                <ul className="space-y-1 text-gray-600 text-sm">
                  <li>• Milan</li>
                  <li>• Monaco</li>
                  <li>• Venise</li>
                  <li>• Aoste</li>
                  <li>• Barcelone</li>
                  <li>• Marseille</li>
                  <li>• Lyon</li>
                  <li>• Paris</li>
                  <li>• Berlin</li>
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              RÉSERVEZ VOTRE TRANSPORT
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              Contactez-nous dès maintenant pour réserver votre service de transport premium. Notre équipe est disponible 24h/24 et 7j/7 pour répondre à tous vos besoins.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-10">
            {/* Contact Form */}
            <Card className="p-6 border shadow-lg">
              <CardHeader className="px-0 pt-0 pb-4">
                <CardTitle className="text-xl text-gray-900 font-bold">Demande de Devis</CardTitle>
              </CardHeader>
              <CardContent className="px-0">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom complet <span className="text-red-500">*</span>
                    </label>
                    <Input
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="border-gray-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Téléphone <span className="text-red-500">*</span>
                    </label>
                    <Input
                      required
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="border-gray-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <Input
                      required
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="border-gray-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Service souhaité
                    </label>
                    <Select value={formData.service} onValueChange={(value) => setFormData({ ...formData, service: value })}>
                      <SelectTrigger className="border-gray-300">
                        <SelectValue placeholder="Choisir un service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="transferts-aeroport">TRANSFERTS AÉROPORT</SelectItem>
                        <SelectItem value="service-taxi">SERVICE TAXI</SelectItem>
                        <SelectItem value="evenements">ÉVÉNEMENTS & PLUS</SelectItem>
                        <SelectItem value="excursion">Excursion Touristique</SelectItem>
                        <SelectItem value="transport-scolaire">Transport Scolaire Sécurisé</SelectItem>
                        <SelectItem value="vehicules-pmr">Véhicules Adaptés PMR</SelectItem>
                        <SelectItem value="autre">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date souhaitée
                    </label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="border-gray-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Message
                    </label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={3}
                      className="border-gray-300"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-[#B8935C] hover:bg-[#A07D4A] text-white font-semibold"
                  >
                    Envoyer ma demande
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <div className="space-y-6">
              <Card className="p-6 bg-[#B8935C] text-white shadow-lg">
                <h3 className="text-2xl font-bold mb-4">Réservation Immédiate</h3>
                <a href="tel:0797796383" className="flex items-center text-3xl font-bold hover:text-gray-100 transition-colors mb-2">
                  <Phone className="w-8 h-8 mr-3" />
                  079 779 63 83
                </a>
                <p className="text-white/90 text-sm ml-11">
                  Service Disponible 7j/7
                </p>
              </Card>

              <Card className="p-6 border shadow-lg">
                <div className="space-y-4">
                  <div className="flex items-start">
                    <Mail className="w-5 h-5 text-[#B8935C] mr-3 mt-0.5" />
                    <div>
                      <h4 className="font-bold text-gray-900 text-sm mb-0.5">Email</h4>
                      <a href="mailto:info@taxis-aigle.ch" className="text-gray-600 hover:text-[#B8935C] transition-colors text-sm">
                        info@taxis-aigle.ch
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <MapPin className="w-5 h-5 text-[#B8935C] mr-3 mt-0.5" />
                    <div>
                      <h4 className="font-bold text-gray-900 text-sm mb-0.5">Adresse</h4>
                      <p className="text-gray-600 text-sm">1860 Aigle, Suisse</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Clock className="w-5 h-5 text-[#B8935C] mr-3 mt-0.5" />
                    <div>
                      <h4 className="font-bold text-gray-900 text-sm mb-0.5">Horaires</h4>
                      <p className="text-gray-600 text-sm">Service Disponible 7j/7</p>
                      <p className="text-gray-600 text-sm">Bureau: Lun-Ven 8h-18h</p>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="p-6 border-2 border-[#B8935C] bg-[#B8935C]/5 shadow-lg">
                <h4 className="font-bold text-base text-gray-900 mb-2">Réponse Garantie</h4>
                <p className="text-gray-600 text-sm">
                  Nous nous engageons à répondre à toutes les demandes dans un <strong className="text-[#B8935C]">délai rapide</strong>.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center">
              <img
                src="https://customer-assets.emergentagent.com/job_aigle-taxis/artifacts/wshzq3zf_taxi_aigle_logo_transparent_cropped.png"
                alt="TAXI AIGLE"
                className="h-10 w-auto brightness-200"
              />
            </div>
            <div className="text-center md:text-right">
              <p className="text-gray-400 text-sm">
                © 2025 Taxi Aigle. Tous droits réservés.
              </p>

            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;